import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

const serviceOptions = ["Agency Showreel", "Social Media Content", "Explainer Video", "Monthly Retainer", "3D & Animation", "Other"];
const budgetOptions = ["Under £1,000", "£1,000 - £2,500", "£2,500 - £5,000", "£5,000+", "Not sure yet"];

export default function Contact() {
  const [formData, setFormData] = useState({ name: "", email: "", company: "", service_interest: "", budget: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.service_interest || !formData.message) { toast.error("Please fill in all required fields"); return; }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent! I'll get back to you soon.");
      setFormData({ name: "", email: "", company: "", service_interest: "", budget: "", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="min-h-screen pt-20 bg-[#F8F6F3]" data-testid="contact-page">
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div {...fadeUp}>
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1C1C1C]/40 mb-6">§ 05 / Contact</p>
              <h1 className="text-4xl md:text-6xl font-black tracking-tight text-[#1C1C1C] mb-2">
                Let's work<br /><em className="not-italic text-[#FF5E3A]">together.</em>
              </h1>
              <div className="w-24 h-1 bg-[#FF5E3A] mt-8 mb-10" />
              <p className="text-base md:text-lg leading-relaxed text-[#1C1C1C]/50 mb-12">Have a project in mind? Fill out the form and I'll get back to you within 24 hours.</p>
              <div className="space-y-6">
                <a href="mailto:elliotshaw92@gmail.com" className="flex items-center gap-4 text-[#1C1C1C] hover:text-[#FF5E3A] transition-colors group" data-testid="contact-email">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#FF5E3A]/10 group-hover:bg-[#FF5E3A] transition-colors">
                    <Mail size={20} className="text-[#FF5E3A] group-hover:text-white" />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1C1C1C]/30 block mb-1">Email</span>
                    <span className="font-medium">elliotshaw92@gmail.com</span>
                  </div>
                </a>
                <a href="tel:07487510819" className="flex items-center gap-4 text-[#1C1C1C] hover:text-[#FF5E3A] transition-colors group" data-testid="contact-phone">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#FF5E3A]/10 group-hover:bg-[#FF5E3A] transition-colors">
                    <Phone size={20} className="text-[#FF5E3A] group-hover:text-white" />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1C1C1C]/30 block mb-1">Phone</span>
                    <span className="font-medium">07487 510819</span>
                  </div>
                </a>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#FF5E3A]/10">
                    <MapPin size={20} className="text-[#FF5E3A]" />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1C1C1C]/30 block mb-1">Location</span>
                    <span className="font-medium text-[#1C1C1C]">Formby, Merseyside, UK</span>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div {...fadeUp} transition={{ delay: 0.15, duration: 0.6 }}>
              {submitted ? (
                <div className="bg-[#1C1C1C] rounded-2xl p-8 md:p-12 text-center text-white">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#FF5E3A] rounded-full mx-auto mb-6"><Send size={28} className="text-white" /></div>
                  <h3 className="text-2xl font-bold mb-4">Message Sent!</h3>
                  <p className="text-white/60 mb-8">Thanks for reaching out. I'll get back to you within 24 hours.</p>
                  <button onClick={() => setSubmitted(false)} className="bg-[#FF5E3A] text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white hover:text-[#1C1C1C] transition-colors" data-testid="send-another-btn">Send Another</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 md:p-12 space-y-6 border border-[#E8E5E0]" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-[#1C1C1C] font-semibold text-xs uppercase tracking-[0.1em]">Name <span className="text-[#FF5E3A]">*</span></Label>
                      <Input name="name" value={formData.name} onChange={handleChange} placeholder="Your name" className="border-[#E8E5E0] bg-[#F8F6F3] focus:border-[#FF5E3A] focus:ring-1 focus:ring-[#FF5E3A] h-12 rounded-xl" data-testid="contact-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#1C1C1C] font-semibold text-xs uppercase tracking-[0.1em]">Email <span className="text-[#FF5E3A]">*</span></Label>
                      <Input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="your@email.com" className="border-[#E8E5E0] bg-[#F8F6F3] focus:border-[#FF5E3A] focus:ring-1 focus:ring-[#FF5E3A] h-12 rounded-xl" data-testid="contact-email-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1C1C1C] font-semibold text-xs uppercase tracking-[0.1em]">Company / Agency</Label>
                    <Input name="company" value={formData.company} onChange={handleChange} placeholder="Your agency name" className="border-[#E8E5E0] bg-[#F8F6F3] focus:border-[#FF5E3A] focus:ring-1 focus:ring-[#FF5E3A] h-12 rounded-xl" data-testid="contact-company-input" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-[#1C1C1C] font-semibold text-xs uppercase tracking-[0.1em]">Service <span className="text-[#FF5E3A]">*</span></Label>
                      <Select value={formData.service_interest} onValueChange={(v) => setFormData({ ...formData, service_interest: v })}>
                        <SelectTrigger className="border-[#E8E5E0] bg-[#F8F6F3] h-12 rounded-xl" data-testid="contact-service-select"><SelectValue placeholder="Select a service" /></SelectTrigger>
                        <SelectContent className="bg-white border-[#E8E5E0] rounded-xl">{serviceOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#1C1C1C] font-semibold text-xs uppercase tracking-[0.1em]">Budget</Label>
                      <Select value={formData.budget} onValueChange={(v) => setFormData({ ...formData, budget: v })}>
                        <SelectTrigger className="border-[#E8E5E0] bg-[#F8F6F3] h-12 rounded-xl" data-testid="contact-budget-select"><SelectValue placeholder="Budget range" /></SelectTrigger>
                        <SelectContent className="bg-white border-[#E8E5E0] rounded-xl">{budgetOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1C1C1C] font-semibold text-xs uppercase tracking-[0.1em]">Project Details <span className="text-[#FF5E3A]">*</span></Label>
                    <Textarea name="message" value={formData.message} onChange={handleChange} placeholder="Tell me about your project..." rows={5} className="border-[#E8E5E0] bg-[#F8F6F3] focus:border-[#FF5E3A] focus:ring-1 focus:ring-[#FF5E3A] resize-none rounded-xl" data-testid="contact-message-input" />
                  </div>
                  <button type="submit" disabled={isSubmitting} className="w-full bg-[#FF5E3A] text-white font-semibold text-sm h-14 rounded-full hover:bg-[#1C1C1C] transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="contact-submit-btn">
                    {isSubmitting ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : <>Send Message <Send size={16} /></>}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
